// src/components/training/ft/FtForm.jsx

import React, {useEffect, useState, useCallback, useRef} from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    CircularProgress,
    Paper,
    IconButton,
    Chip,
    Stack,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CloudUpload as CloudUploadIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {useLocation, useNavigate} from 'react-router-dom';

import TrainingFormHeader from '../../TrainingFormHeader';
import TrainingFormSection from '../../TrainingFormSection';
import SignatureModalWithAuto from '../../../common/SignatureModalWithAuto';

import {
    saveFtForm,
    markFtFormStatus,
    getAllFtForms
} from '../../../../services/ftFormService';

import {
    STATUS_FINISH,
    STATUS_PROCESS,
    STATUS_SEND
} from '../../../../config/constants';

import axiosInstance from '../../../../axiosInstance';
import { hasSignatureContent } from '../../../../utils/utils';
import { localDateString } from '../../../../utils/localDate';

// Pulsing upload button style
const pulsingUploadButton = {
    position: 'relative',
    overflow: 'visible',
    animation: 'pulse 2s ease-in-out infinite',
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
        },
        '50%': {
            transform: 'scale(1.05)',
            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
        },
    },
};

/**
 * Format date helper
 */
const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Not sent yet';
    
    if (typeof dateValue === 'object' && dateValue?.date) {
        return new Date(dateValue.date).toLocaleString('en-US');
    }
    
    if (dateValue) {
        return new Date(dateValue).toLocaleString('en-US');
    }
    
    return 'Not sent yet';
};

export default function FtForm() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const selectedTrainingDue = state?.selectedTrainingDue || null;
    const trainingDueIdFromRoute = selectedTrainingDue?.id || state?.trainingDueId || null;

    const sigPadRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    const defaultFormData = {
        operatorName: selectedTrainingDue?.fullName || '',
        trainerName: '',
        date: localDateString(),
        course: selectedTrainingDue?.course || '',
        sendBy: localStorage.getItem('userId'),
        
        // Section 7
        section7: {
            clutch: false,
            tiltControl: false,
            attachmentControl: false,
            serviceBrake: false,
            inchingControl: false,
            liftControl: false,
            steeringTechniques: false,
            otherEnabled: false,
            other: '',
        },

        // Section 8
        section8: {
            within25Feet: false,
            over25FeetNotInView: false,
            positionControlNotInView: false,
            useChocksOnIncline: false,
        },

        // Section 9
        section9: {
            properMounting: false,
            seatBelts: false,
            directionOfTravel: false,
            carryingForksLowered: false,
            appropriateSpeeds: false,
            carryingLoadUp: false,
            carryingLoadDown: false,
            adjustmentWetSurface: false,
            cornering: false,
            soundingHorn: false,
        },

        // Section 10
        section10: {
            properBalancing: false,
            properSize: false,
            properCapacity: false,
            loadCradledBack: false,
            otherEnabled: false,
            other: '',
        },

        // Section 11
        section11: {
            maintainingProper: false,
            centeringLoads: false,
            HandlingLoad: false,
            smoothStartingBraking: false,
            travelingThreeForkLengths: false,
            otherEnabled: false,
            other: '',
        },

        // Section 12
        section12: {
            stackingSquarely: false,
            depositingLoadSafely: false,
            properHeightTieredLoad: false,
            properlyDepositing: false,
            removingLoadTravel: false,
            usingProper: false,
            removingLoadRack: false,
            otherEnabled: false,
            other: '',
        },

        // Section 13
        section13: {
            demonstratesParking: false,
            demonstratesLoadPlacement: false,
            awareDangerousHazards: false,
            areasNotPlaceLoads: false,
            awareFireEquipment: false,
            awareEmployees: false,
            awareAisles: false,
            awareExits: false,
            otherEnabled: false,
            other: '',
        },

        // Section 14
        section14: {
            success: false,
        },

        completed: false,
        signature: null,
        status: STATUS_PROCESS,
        trainingDueId: null,
        lastSentAt: null,
    };

    const [formData, setFormData] = useState(null);
    const [showSignatureModal, setShowSignatureModal] = useState(false);


    /**
     * Load/create FT form from IndexedDB
     */
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const allForms = await getAllFtForms();
                const activeForms = allForms.filter(f => f.status !== STATUS_SEND);

                let foundForm = null;

                if (trainingDueIdFromRoute) {
                    foundForm = activeForms.find(f => f.trainingDueId === trainingDueIdFromRoute);
                    if (!foundForm) {
                        foundForm = {
                            ...defaultFormData,
                            trainingDueId: trainingDueIdFromRoute,
                            id: Date.now(),
                        };
                        await saveFtForm(foundForm);
                    }
                } else {
                    foundForm = activeForms.find(f => f.operatorName === (selectedTrainingDue?.fullName || ''));
                    if (!foundForm) {
                        foundForm = {
                            ...defaultFormData,
                            id: Date.now(),
                        };
                        await saveFtForm(foundForm);
                    }
                }

                // Check if section9 has old fields and reset if needed
                if (foundForm.section9 && ('loadChartSpecific' in foundForm.section9 || 
                    'stabilityTriangle' in foundForm.section9 || 
                    'attachmentsUsed' in foundForm.section9)) {
                    // Old structure detected, reset section9
                    foundForm.section9 = defaultFormData.section9;
                }
                
                // Check if section10 has old fields and reset if needed  
                if (foundForm.section10 && ('mountingAndDismounting' in foundForm.section10 || 
                    'maneuvering' in foundForm.section10)) {
                    // Old structure detected, reset section10
                    foundForm.section10 = defaultFormData.section10;
                }
                
                // Check if section11 has old fields and reset if needed
                if (foundForm.section11 && ('ramps' in foundForm.section11 || 
                    'inclines' in foundForm.section11)) {
                    // Old structure detected, reset section11
                    foundForm.section11 = defaultFormData.section11;
                }
                
                // Check if section12 has old fields and reset if needed
                if (foundForm.section12 && 'success' in foundForm.section12 && 
                    Object.keys(foundForm.section12).length === 1) {
                    // Old structure detected (only had success field), reset section12
                    foundForm.section12 = defaultFormData.section12;
                }
                
                // Ensure all sections exist with default values if missing
                const ensuredForm = {
                    ...foundForm,
                    section7: foundForm.section7 || defaultFormData.section7,
                    section8: foundForm.section8 || defaultFormData.section8,
                    section9: foundForm.section9 || defaultFormData.section9,
                    section10: foundForm.section10 || defaultFormData.section10,
                    section11: foundForm.section11 || defaultFormData.section11,
                    section12: foundForm.section12 || defaultFormData.section12,
                    section13: foundForm.section13 || defaultFormData.section13,
                    section14: foundForm.section14 || defaultFormData.section14,
                };
                
                // Save the migrated form back to IndexedDB
                await saveFtForm(ensuredForm);

                setFormData(ensuredForm);
            } catch (error) {
                console.error('Error loading FT form:', error);
                setFormData({ ...defaultFormData, id: Date.now() });
            } finally {
                setLoading(false);
            }
        })();
    }, [trainingDueIdFromRoute, selectedTrainingDue]);

    /**
     * Handle input change
     */
    const handleInputChange = useCallback((field, value) => {
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
        saveFtForm(updatedData);
    }, [formData]);

    /**
     * Handle section field change
     */
    const handleFieldChange = useCallback((section, field, value) => {
        const updatedData = {
            ...formData,
            [section]: {
                ...formData[section],
                [field]: value,
            }
        };
        setFormData(updatedData);
        saveFtForm(updatedData);
    }, [formData]);

    /**
     * Save signature
     */
    const handleSaveSignature = useCallback(async () => {
        if (hasSignatureContent(sigPadRef)) {
            const signatureData = sigPadRef.current.toDataURL();
            const updatedData = {
                ...formData,
                signature: signatureData,
                completed: true,
                status: STATUS_PROCESS,
            };
            setFormData(updatedData);
            await saveFtForm(updatedData);
            setShowSignatureModal(false);
        }
    }, [formData]);

    /**
     * Send form
     */
    const handleSend = useCallback(async () => {
        if (!formData.signature || !formData.completed) {
            alert('Please complete and sign the form before sending.');
            return;
        }

        setIsUploading(true);
        try {
            const payload = {
                ...formData,
                dateDue: selectedTrainingDue?.dateDue,
                dateThru: selectedTrainingDue?.dateThru,
                formType: 'ft',
            };

            const response = await axiosInstance.post('/training-due/upload', payload);

            if (response.status === 200) {
                const updatedData = {
                    ...formData,
                    status: STATUS_SEND,
                    lastSentAt: new Date().toISOString(),
                };
                await markFtFormStatus(formData.id, STATUS_SEND);
                setFormData(updatedData);
                alert('Form sent successfully!');
                navigate('/training-due');
            } else {
                throw new Error(`Server responded with status ${response.status}`);
            }
        } catch (error) {
            console.error('Error sending form:', error);
            const updatedData = {
                ...formData,
                status: STATUS_FINISH,
            };
            await markFtFormStatus(formData.id, STATUS_FINISH);
            setFormData(updatedData);
            alert('Failed to send form but saved locally.');
        } finally {
            setIsUploading(false);
        }
    }, [formData, selectedTrainingDue, navigate]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!formData) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Failed to load form data</Alert>
            </Box>
        );
    }

    const canSend = formData.signature && formData.completed;
    const isReadyToSend = formData.status === STATUS_PROCESS && canSend;

    // Get status color
    const getStatusColor = () => {
        switch(formData.status) {
            case STATUS_PROCESS: return 'warning';
            case STATUS_SEND: return 'success';
            case STATUS_FINISH: return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            bgcolor: 'background.default'
        }}>
            {/* Fixed Header */}
            <Paper 
                elevation={2}
                sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    borderRadius: 0,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <IconButton 
                                onClick={() => navigate('/training-due')}
                                size="large"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                    }
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    Forklift Training Form
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={formData.status?.toUpperCase() || 'NEW'}
                                        color={getStatusColor()}
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                        {formData.date}
                                    </Typography>
                                    {formData.operatorName && (
                                        <Typography variant="caption" color="text.secondary">
                                            • {formData.operatorName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>

                        {/* Send Button in Header */}
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                variant="contained"
                                color={isReadyToSend ? "error" : "inherit"}
                                onClick={handleSend}
                                disabled={!canSend || isUploading}
                                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                sx={isReadyToSend && !isUploading ? pulsingUploadButton : {}}
                            >
                                {isUploading ? 'Sending...' : 'Send'}
                            </Button>
                            {formData.lastSentAt && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        position: 'absolute',
                                        bottom: -20,
                                        right: 0,
                                        whiteSpace: 'nowrap',
                                        color: 'text.secondary'
                                    }}
                                >
                                    Last sent: {formatDateTime(formData.lastSentAt)}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Form Header */}
                <TrainingFormHeader
                    onGoBack={'#/training-due'}
                    operatorName={formData.operatorName}
                    trainerName={formData.trainerName}
                    date={formData.date}
                    onInputChange={handleInputChange}
                    readOnly={!!selectedTrainingDue}
                    maxDate={''}
                    minDate={''}
                />

                {/* Section 7 - Controls */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Ability to demonstrate and explain proper use of controls:"
                        fields={[
                            { key: 'clutch', label: 'Clutch and/or Torque Converter', type: 'checkbox' },
                            { key: 'tiltControl', label: 'Tilt Control', type: 'checkbox' },
                            { key: 'attachmentControl', label: 'Attachment Control', type: 'checkbox' },
                            { key: 'serviceBrake', label: 'Service Brake', type: 'checkbox' },
                            { key: 'inchingControl', label: 'Inching Control', type: 'checkbox' },
                            { key: 'liftControl', label: 'Lift Control', type: 'checkbox' },
                            { key: 'steeringTechniques', label: 'Proper Steering Techniques', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section7}
                        onFieldChange={(key, value) => handleFieldChange('section7', key, value)}
                    />
                </Paper>

                {/* Section 8 - Proper positioning of controls */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Proper positioning of controls:"
                        fields={[
                            { key: 'within25Feet', label: 'Proper positioning of controls when within 25 feet from truck and in view', type: 'checkbox' },
                            { key: 'over25FeetNotInView', label: 'Proper positioning of controls when over 25 feet from truck and not in view', type: 'checkbox' },
                            { key: 'positionControlNotInView', label: 'Proper position of control when not in view regardless of distance', type: 'checkbox' },
                            { key: 'useChocksOnIncline', label: 'Use of chocks when parked on an incline', type: 'checkbox' },
                        ]}
                        values={formData.section8}
                        onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                    />
                </Paper>

                {/* Section 9 - Demonstration of proper forklift operation */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Demonstration of proper forklift operation:"
                        fields={[
                            { key: 'properMounting', label: 'Proper mounting and dismounting', type: 'checkbox' },
                            { key: 'seatBelts', label: 'Use of seat belts', type: 'checkbox' },
                            { key: 'directionOfTravel', label: 'Looking in the direction of travel', type: 'checkbox' },
                            { key: 'carryingForksLowered', label: 'Carrying forks in lowered position', type: 'checkbox' },
                            { key: 'appropriateSpeeds', label: 'Appropriate speeds', type: 'checkbox' },
                            { key: 'carryingLoadUp', label: 'Carrying load uphill with load pointing uphill', type: 'checkbox' },
                            { key: 'carryingLoadDown', label: 'Carrying load down incline with load pointing uphill', type: 'checkbox' },
                            { key: 'adjustmentWetSurface', label: 'Proper adjustment to wet or uneven driving surface', type: 'checkbox' },
                            { key: 'cornering', label: 'Cornering', type: 'checkbox' },
                            { key: 'soundingHorn', label: 'Sounding horn at corners and intersections', type: 'checkbox' },
                        ]}
                        values={formData.section9}
                        onFieldChange={(key, value) => handleFieldChange('section9', key, value)}
                    />
                </Paper>

                {/* Section 10 - Loading */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Loading:"
                        fields={[
                            { key: 'properBalancing', label: 'Proper balancing of load', type: 'checkbox' },
                            { key: 'properSize', label: 'Proper size load for safe handling and visibility', type: 'checkbox' },
                            { key: 'properCapacity', label: 'Proper capacity for truck size and type', type: 'checkbox' },
                            { key: 'loadCradledBack', label: 'Load cradled back against backrest', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other description', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section10}
                        onFieldChange={(key, value) => handleFieldChange('section10', key, value)}
                    />
                </Paper>

                {/* Section 11 - Driving with load */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Driving with load:"
                        fields={[
                            { key: 'maintainingProper', label: 'Maintaining proper speed for load size', type: 'checkbox' },
                            { key: 'centeringLoads', label: 'Centering loads on forks', type: 'checkbox' },
                            { key: 'HandlingLoad', label: 'Handling load to prevent harm to cargo', type: 'checkbox' },
                            { key: 'smoothStartingBraking', label: 'Performing smooth starting and braking', type: 'checkbox' },
                            { key: 'travelingThreeForkLengths', label: 'Traveling at least three fork truck lengths behind other trucks', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other description', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section11}
                        onFieldChange={(key, value) => handleFieldChange('section11', key, value)}
                    />
                </Paper>

                {/* Section 12 - Unloading and Stacking */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Unloading and Stacking:"
                        fields={[
                            { key: 'stackingSquarely', label: 'Stacking squarely and straight', type: 'checkbox' },
                            { key: 'depositingLoadSafely', label: 'Depositing load safely', type: 'checkbox' },
                            { key: 'properHeightTieredLoad', label: 'Proper height of tiered load', type: 'checkbox' },
                            { key: 'properlyDepositing', label: 'Properly removing load from rack', type: 'checkbox' },
                            { key: 'removingLoadTravel', label: 'Removing load & lowering to safe level before starting to travel', type: 'checkbox' },
                            { key: 'usingProper', label: 'Using proper fork spread when picking load', type: 'checkbox' },
                            { key: 'removingLoadRack', label: 'Properly removing load from rack', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other description', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section12}
                        onFieldChange={(key, value) => handleFieldChange('section12', key, value)}
                    />
                </Paper>

                {/* Section 13 - Workplace conditions */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Workplace conditions:"
                        fields={[
                            { key: 'demonstratesParking', label: 'Demonstrates proper forklift parking', type: 'checkbox' },
                            { key: 'demonstratesLoadPlacement', label: 'Demonstrates proper load placement', type: 'checkbox' },
                            { key: 'awareDangerousHazards', label: 'Aware of dangerous or hazardous areas', type: 'checkbox' },
                            { key: 'areasNotPlaceLoads', label: 'Aware of areas not to place loads', type: 'checkbox' },
                            { key: 'awareFireEquipment', label: 'Aware of fire equipment & appropriate eyewash & 1st aid facilities', type: 'checkbox' },
                            { key: 'awareEmployees', label: 'Aware of other employees', type: 'checkbox' },
                            { key: 'awareAisles', label: 'Aware of aisles for egress', type: 'checkbox' },
                            { key: 'awareExits', label: 'Aware of exits', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other description', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section13}
                        onFieldChange={(key, value) => handleFieldChange('section13', key, value)}
                    />
                </Paper>

                {/* Section 14 - Successfully completed maneuvering practice drills */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Final evaluation:"
                        fields={[
                            { key: 'success', label: 'Successfully completed maneuvering practice drills', type: 'checkbox' },
                        ]}
                        values={formData.section14}
                        onFieldChange={(key, value) => handleFieldChange('section14', key, value)}
                    />
                </Paper>

                {/* Signature Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Operator Signature
                            </Typography>
                            {formData.signature ? (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                    <CheckCircleIcon color="success" />
                                    <Typography color="success.main">
                                        Signed
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography color="text.secondary" sx={{ mt: 1 }}>
                                    Not signed yet
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setShowSignatureModal(true)}
                        >
                            {formData.signature ? 'Update Signature' : 'Add Signature'}
                        </Button>
                    </Stack>
                    
                    {formData.signature && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <img 
                                src={formData.signature} 
                                alt="Signature" 
                                style={{ maxWidth: '300px', height: 'auto' }}
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                sigPadRef={sigPadRef}
                employeeName={formData.operatorName || 'Operator'}
                onAccept={handleSaveSignature}
            />
        </Box>
    );
}